import { Box, Checkbox, FormControlLabel, Typography } from '@mui/material';
import { ClassNameMap } from '@mui/styles';

import { TableFilter } from '../../Table.types';

type DualMultiSelectFilterControlProps = {
  filter: TableFilter;
  activeFilters: Record<string, string>;
  classes: ClassNameMap;
  onFilterChange: (condition: string, value: any) => any;
};

export const DualMultiSelectFilterControl = ({
  filter,
  activeFilters,
  classes,
  onFilterChange,
}: DualMultiSelectFilterControlProps) => {
  // Get the three column IDs
  const [leftOnlyId, rightOnlyId, bothSelectedId] = filter.columnIds || [];

  // Check which filter is currently active
  const leftOnlyValue = activeFilters[leftOnlyId] || '';
  const rightOnlyValue = activeFilters[rightOnlyId] || '';
  const bothValue = activeFilters[bothSelectedId] || '';

  // Parse current selections based on which filter is active
  let leftSelected: string[] = [];
  let rightSelected: string[] = [];

  if (bothValue) {
    // Combined format: "Perform-MatchState-Covered,IMG-Video-Covered"
    const values = bothValue.split(',');
    values.forEach(value => {
      if (value.includes('-')) {
        const [left, ...rightParts] = value.split('-');
        leftSelected.push(left);
        rightSelected.push(rightParts.join('-'));
      }
    });
    // Remove duplicates
    leftSelected = [...new Set(leftSelected)];
    rightSelected = [...new Set(rightSelected)];
  } else if (leftOnlyValue) {
    leftSelected = leftOnlyValue.split(',');
  } else if (rightOnlyValue) {
    rightSelected = rightOnlyValue.split(',');
  }

  const handleCheckboxChange = (value: string, checked: boolean, isLeft: boolean) => {
    let newLeftSelected = [...leftSelected];
    let newRightSelected = [...rightSelected];

    if (isLeft) {
      if (checked) {
        newLeftSelected.push(value);
      } else {
        newLeftSelected = newLeftSelected.filter(v => v !== value);
      }
    } else {
      if (checked) {
        newRightSelected.push(value);
      } else {
        newRightSelected = newRightSelected.filter(v => v !== value);
      }
    }

    // Clear all previous filters
    onFilterChange(leftOnlyId, '');
    onFilterChange(rightOnlyId, '');
    onFilterChange(bothSelectedId, '');

    // Determine which column ID to use based on selections
    if (newLeftSelected.length > 0 && newRightSelected.length > 0) {
      // Both selected - combine with dash separator, use bothSelectedId
      const combinations = [];
      newLeftSelected.forEach(left => {
        newRightSelected.forEach(right => {
          combinations.push(`${left}-${right}`);
        });
      });
      onFilterChange(bothSelectedId, combinations.join(','));
    } else if (newLeftSelected.length > 0) {
      // Only left selected - use leftOnlyId
      onFilterChange(leftOnlyId, newLeftSelected.join(','));
    } else if (newRightSelected.length > 0) {
      // Only right selected - use rightOnlyId
      onFilterChange(rightOnlyId, newRightSelected.join(','));
    }
  };

  return (
    <Box display="flex" className={classes.multiselectContainer} style={{ gap: '16px' }}>
      {/* Left column */}
      <Box style={{ flex: 1 }}>
        <Typography variant="subtitle2" style={{ marginBottom: '8px', fontWeight: 'bold' }}>
          Source
        </Typography>
        <Box display="flex" flexDirection="column">
          {filter.leftValues?.map((value, index) => (
            <FormControlLabel
              key={`left-${index}-${value}`}
              control={
                <Checkbox
                  data-testid={`dual-multiselect-left-${value}`}
                  checked={leftSelected.includes(String(value))}
                  onChange={e => handleCheckboxChange(String(value), e.target.checked, true)}
                  color="primary"
                  size="small"
                />
              }
              label={String(value)}
            />
          ))}
        </Box>
      </Box>

      {/* Right column */}
      <Box style={{ flex: 1 }}>
        <Typography variant="subtitle2" style={{ marginBottom: '8px', fontWeight: 'bold' }}>
          Type
        </Typography>
        <Box display="flex" flexDirection="column">
          {filter.rightValues?.map((value, index) => (
            <FormControlLabel
              key={`right-${index}-${value}`}
              control={
                <Checkbox
                  data-testid={`dual-multiselect-right-${value}`}
                  checked={rightSelected.includes(String(value))}
                  onChange={e => handleCheckboxChange(String(value), e.target.checked, false)}
                  color="primary"
                  size="small"
                />
              }
              label={String(value)}
            />
          ))}
        </Box>
      </Box>
    </Box>
  );
};
